import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import moment from "moment";
import { IconDownload } from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { PeriodFilter } from "./periodFilter";
import { useServerApi } from "../hooks/userServerApi";
import { useFormatter } from "../hooks/useFomatter";
import { useCellRender } from "../hooks/useCellRender";
import GISettlementCrossCheckRow from "./giSettlementCrossCheckRow";
import {
  CONSULTANT_DN_STATUS_OPTIONS,
  CONSULTANT_PAYMENT_STATUS_OPTIONS,
  CONSULTANT_POLICY_STATUS_OPTIONS,
  DEFAULT_CONSULTANT_FILTERS,
  extractClientOptionsFromRows,
  filterGroupsByBucket,
  filterRowsByClient,
  filterRowsByOverdueBucket,
  formatBucketCountAmount,
  getAllConsultantDashboardRows,
  formatOverdueBucketAmounts,
  getOverdueSummaryColor,
  getOverdueSummaryLabelKey,
  GI_CONSULTANT_OVERDUE_BUCKETS,
  GI_CONSULTANT_SUMMARY_BUCKETS,
  GI_OVERDUE_DASHBOARD_BUCKETS,
  GI_OVERDUE_SUMMARY_BUCKETS,
} from "../pages/gi/giOverdueDashboardUtils";
import EntityTabAnchor from "../pages/account/payroll2/EntityTabAnchor";
import { openFileUrl } from "../utils/openReport";

const OverdueSummaryCard = ({
  label,
  value,
  amount,
  color,
  active,
  onClick,
}) => (
  <Card
    withBorder
    padding="sm"
    radius="md"
    onClick={onClick}
    sx={{
      minWidth: 0,
      overflow: "hidden",
      cursor: onClick ? "pointer" : undefined,
      ...(color &&
        (active
          ? {
              border: `2px solid var(--mantine-color-${color}-6)`,
            }
          : {
              borderLeft: `4px solid var(--mantine-color-${color}-6)`,
            })),
    }}
  >
    <Stack spacing={4}>
      <Title order={2} color={color} sx={{ lineHeight: 1 }}>
        {value ?? 0}
      </Title>
      <Text size="xs" fw={600} sx={{ lineHeight: 1.3, wordBreak: "break-word" }}>
        {amount}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Stack>
  </Card>
);

const OverdueSummarySection = ({
  summary,
  activeBucket,
  onBucketChange,
  summaryBuckets,
  bucketDefinitions,
  formatter,
  t,
}) => (
  <Stack spacing="xs">
    <Text size="xs" c="dimmed" fw={500}>
      {t("dashboard.giOverdue.overdueTitle", "Overdue severity")}
    </Text>
    <SimpleGrid
      cols={5}
      breakpoints={[
        { maxWidth: "md", cols: 2 },
        { maxWidth: "sm", cols: 1 },
      ]}
    >
      {summaryBuckets.map((bucket) => {
        const color = getOverdueSummaryColor(bucket.id);
        const bucketSummary = summary[bucket.id] || {
          count: 0,
          amountsByCurrency: {},
        };
        return (
          <OverdueSummaryCard
            key={bucket.id}
            label={t(getOverdueSummaryLabelKey(bucket.id), bucket.label)}
            value={bucketSummary.count}
            amount={formatOverdueBucketAmounts(
              bucketSummary.amountsByCurrency,
              formatter.currency
            )}
            color={color}
            active={activeBucket === bucket.id}
            onClick={() => onBucketChange(bucket.id)}
          />
        );
      })}
    </SimpleGrid>
    <Text size="xs" c="dimmed" sx={{ minHeight: 18 }}>
      {activeBucket !== "all" ? (
        <>
          {t("dashboard.giOverdue.filterActive", "Filtering by")}{" "}
          {t(
            getOverdueSummaryLabelKey(activeBucket),
            bucketDefinitions.find((b) => b.id === activeBucket)?.label
          )}
          {" · "}
          <Text
            component="span"
            size="xs"
            c="blue"
            style={{ cursor: "pointer" }}
            onClick={() => onBucketChange("all")}
          >
            {t("dashboard.giOverdue.clearFilter", "Clear filter")}
          </Text>
        </>
      ) : (
        t("dashboard.giOverdue.showingAll", "Showing all overdue buckets")
      )}
    </Text>
  </Stack>
);

const GiOverdueConsultantFilterBar = ({
  filters,
  onChange,
  clientOptions,
  selectedClients,
  onClientChange,
  onRefresh,
  onDownload,
  loading,
  downloadLoading,
  showIncludeBottomLine,
  includeBottomLine,
  onIncludeBottomLineChange,
  t,
}) => (
  <Stack spacing="sm">
    <Group spacing="sm" align="flex-end" noWrap sx={{ width: "100%" }}>
      <MultiSelect
        size="xs"
        label={t("dashboard.giOverdue.filters.paymentStatus", "Payment Status")}
        data={CONSULTANT_PAYMENT_STATUS_OPTIONS}
        value={filters.paymentStatus}
        onChange={(value) => onChange({ ...filters, paymentStatus: value })}
        searchable
        clearable
        style={{ minWidth: 180 }}
      />
      <MultiSelect
        size="xs"
        label={t("dashboard.giOverdue.filters.dnStatus", "DN Status")}
        data={CONSULTANT_DN_STATUS_OPTIONS}
        value={filters.status}
        onChange={(value) => onChange({ ...filters, status: value })}
        searchable
        clearable
        style={{ minWidth: 180 }}
      />
      <MultiSelect
        size="xs"
        label={t("dashboard.giOverdue.filters.policyStatus", "Policy Status")}
        data={CONSULTANT_POLICY_STATUS_OPTIONS}
        value={filters.policyStatus}
        onChange={(value) => onChange({ ...filters, policyStatus: value })}
        placeholder={t("All", "All")}
        searchable
        clearable
        style={{ minWidth: 180 }}
      />
      <MultiSelect
        size="xs"
        label={t("dashboard.giOverdue.filters.client", "Client")}
        data={clientOptions}
        value={selectedClients}
        onChange={onClientChange}
        placeholder={t("All", "All")}
        searchable
        clearable
        sx={{ flex: 1, minWidth: 180 }}
      />
      <Button size="xs" onClick={onRefresh} loading={loading}>
        {t("dashboard.giOverdue.refresh", "Refresh")}
      </Button>
    </Group>
    <Group spacing="sm" align="center" wrap="wrap">
      <Button
        size="xs"
        variant="light"
        onClick={onDownload}
        loading={downloadLoading}
      >
        {t("dashboard.giOverdue.downloadReport", "Download report")}
      </Button>
      {showIncludeBottomLine ? (
        <Checkbox
          size="xs"
          label={t(
            "dashboard.giOutstanding.includeBottomLineMembers",
            "Include bottom line members"
          )}
          checked={includeBottomLine}
          onChange={(event) =>
            onIncludeBottomLineChange(event.currentTarget.checked)
          }
          styles={{ label: { fontSize: 12 } }}
        />
      ) : null}
    </Group>
  </Stack>
);

const ClientTableCell = ({ row }) => (
  <Stack spacing={2}>
    <Text size="xs">{row.clientName || "-"}</Text>
    {row.clientCode ? (
      <Text size="xs" c="dimmed">
        {row.clientCode}
      </Text>
    ) : null}
  </Stack>
);

const GroupDetailPanel = ({ group, activeBucket, t, hideInsurer }) => {
  const detailRows = useMemo(
    () => filterRowsByOverdueBucket(group.rows, activeBucket),
    [group.rows, activeBucket]
  );

  if (!detailRows.length) {
    return (
      <Text size="sm" c="dimmed" p="sm">
        {t(
          "giSettlement.crossCheck.noRowsInBucket",
          "No rows in this overdue bucket."
        )}
      </Text>
    );
  }

  return (
    <ScrollArea type="auto">
      <Table fontSize="sm" striped highlightOnHover>
        <thead>
          <tr>
            <th style={{ width: 40, minWidth: 40 }}>
              <Text size="xs" ta="center">
                #
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("giSettlement.crossCheck.columns.policyNo", "Policy No")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("giSettlement.crossCheck.columns.statement", "Statement")}
              </Text>
            </th>
            {!hideInsurer ? (
              <th>
                <Text size="xs">
                  {t("giSettlement.crossCheck.columns.insurer", "Insurer")}
                </Text>
              </th>
            ) : null}
            <th>
              <Text size="xs">
                {t("dashboard.giOverdue.columns.consultants", "Consultants")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t(
                  "dashboard.giOverdue.columns.clientClass",
                  "Client / Class"
                )}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("giSettlement.crossCheck.columns.amount", "Amount")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t(
                  "giSettlement.crossCheck.columns.overdueDays",
                  "Overdue Days"
                )}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("giSettlement.crossCheck.columns.matchStatus", "Status")}
              </Text>
            </th>
            <th style={{ width: 44, minWidth: 44 }}>
              <Text size="xs" ta="center">
                {t("giSettlement.crossCheck.columns.check", "Check")}
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {detailRows.map((row, index) => (
            <GISettlementCrossCheckRow
              key={`${row.policyNumber}-${row.insurerStatementCode}-${index}`}
              row={row}
              index={index}
              t={t}
              hideInsurer={hideInsurer}
              showDebitNoteDetails
            />
          ))}
        </tbody>
      </Table>
    </ScrollArea>
  );
};

const ConsultantDetailPanel = ({
  group,
  activeBucket,
  selectedClients,
  formatter,
  cellRender,
  t,
}) => {
  const detailRows = useMemo(
    () =>
      filterRowsByClient(
        filterRowsByOverdueBucket(
          group.rows,
          activeBucket,
          GI_CONSULTANT_OVERDUE_BUCKETS
        ),
        selectedClients
      ),
    [group.rows, activeBucket, selectedClients]
  );

  if (!detailRows.length) {
    return (
      <Text size="sm" c="dimmed" p="sm">
        {t(
          "giSettlement.crossCheck.noRowsInBucket",
          "No rows in this overdue bucket."
        )}
      </Text>
    );
  }

  return (
    <ScrollArea type="auto">
      <Table fontSize="sm" striped highlightOnHover>
        <thead>
          <tr>
            <th style={{ width: 40, minWidth: 40 }}>
              <Text size="xs" ta="center">
                #
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("dashboard.giOverdue.columns.debitNote", "DN No.")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("dashboard.giOverdue.columns.policyNo", "Policy No.")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("dashboard.giOverdue.columns.client", "Client")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("giSettlement.crossCheck.columns.insurer", "Insurer")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t(
                  "dashboard.giOverdue.columns.outstandingAmount",
                  "Outstanding"
                )}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("dashboard.giOverdue.columns.overdueDays", "Overdue Days")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t(
                  "dashboard.giOverdue.columns.paymentStatus",
                  "Payment Status"
                )}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t("dashboard.giOverdue.columns.dnStatus", "DN Status")}
              </Text>
            </th>
            <th>
              <Text size="xs">
                {t(
                  "dashboard.giOverdue.columns.policyStatus",
                  "Policy Status"
                )}
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {detailRows.map((row, index) => (
            <tr key={`${row.debitNoteId || row.code}-${index}`}>
              <td>
                <Text size="xs" ta="center">
                  {index + 1}
                </Text>
              </td>
              <td>
                <Stack spacing={2}>
                  {row.debitNoteId ? (
                    <EntityTabAnchor
                      entity="debitNote"
                      id={row.debitNoteId}
                      label={row.code}
                      onClick={(event) => event.stopPropagation()}
                    />
                  ) : (
                    <Text size="xs">{row.code}</Text>
                  )}
                  {row.date ? (
                    <Text size="xs" c="dimmed">
                      {moment(row.date).format("YYYY-MM-DD")}
                    </Text>
                  ) : null}
                </Stack>
              </td>
              <td>
                <Text size="xs">{row.policyNumber || "-"}</Text>
              </td>
              <td>
                <ClientTableCell row={row} />
              </td>
              <td>
                <Text size="xs">{row.insurerLabel || "-"}</Text>
              </td>
              <td>
                <Text size="xs">
                  {formatter.currency(
                    row.outstandingAmount,
                    row.currency || "HKD"
                  )}
                </Text>
              </td>
              <td>
                <Text size="xs">{row.overdueDays ?? 0}</Text>
              </td>
              <td>{cellRender.DebitNotePaymentStatus(row.paymentStatus)}</td>
              <td>
                <Text size="xs">{t(row.status || "-")}</Text>
              </td>
              <td>
                <Text size="xs">{t(row.policyStatus || "-")}</Text>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </ScrollArea>
  );
};

const OverdueGroupTable = ({
  groups,
  nameColumnLabel,
  getGroupKey,
  getGroupLabel,
  activeBucket,
  expandedKey,
  onToggle,
  detailHideInsurer,
  emptyMessage,
  formatter,
  summaryBuckets,
  renderDetailPanel,
  t,
}) => {
  if (!groups.length) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        {emptyMessage}
      </Text>
    );
  }

  return (
    <ScrollArea type="auto">
      <Table fontSize="sm" striped highlightOnHover withTableBorder>
        <thead>
          <tr>
            <th>
              <Text size="xs">{nameColumnLabel}</Text>
            </th>
            <th>
              <Text size="xs">
                {t("dashboard.giOverdue.columns.totalOverdue", "Total Overdue")}
              </Text>
            </th>
            {summaryBuckets.map((bucket) => (
              <th key={bucket.id}>
                <Text size="xs">
                  {t(getOverdueSummaryLabelKey(bucket.id), bucket.label)}
                </Text>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => {
            const groupKey = getGroupKey(group);
            const isExpanded = expandedKey === groupKey;

            return (
              <React.Fragment key={groupKey}>
                <tr
                  onClick={() => onToggle(groupKey)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <Text size="sm" fw={500}>
                      {getGroupLabel(group)}
                    </Text>
                  </td>
                  <td>
                    <Text size="sm">
                      {formatBucketCountAmount(
                        group.totalOverdue,
                        formatter.currency
                      )}
                    </Text>
                  </td>
                  {summaryBuckets.map((bucket) => (
                    <td key={bucket.id}>
                      <Text size="sm">
                        {formatBucketCountAmount(
                          group.buckets?.[bucket.id],
                          formatter.currency
                        )}
                      </Text>
                    </td>
                  ))}
                </tr>
                {isExpanded ? (
                  <tr>
                    <td colSpan={summaryBuckets.length + 2}>
                      <Box py="xs">
                        {renderDetailPanel({
                          group,
                          activeBucket,
                          hideInsurer: detailHideInsurer,
                        })}
                      </Box>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </ScrollArea>
  );
};

const DashboardGiOutstandingInsurerTab = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [period, setPeriod] = useState(null);
  const [insurerDashboard, setInsurerDashboard] = useState(null);
  const [insurerLoading, setInsurerLoading] = useState(false);
  const [insurerBucket, setInsurerBucket] = useState("all");
  const [expandedInsurer, setExpandedInsurer] = useState(null);
  const [insurerExporting, setInsurerExporting] = useState(false);

  const yearMonth = useMemo(() => {
    if (!period?.endDate) return moment().format("YYYY-MM");
    return moment(period.endDate).format("YYYY-MM");
  }, [period?.endDate]);

  const insurerRequestIdRef = useRef(0);

  const loadInsurerDashboard = async (month) => {
    if (!month) return;
    const requestId = ++insurerRequestIdRef.current;
    setInsurerLoading(true);
    try {
      const result = await api.GISettlement.getGiOverdueDashboard({
        yearMonth: month,
      });
      if (requestId !== insurerRequestIdRef.current) return;
      if (result?.success !== false && result?.data) {
        setInsurerDashboard(result.data);
      } else {
        setInsurerDashboard(null);
      }
    } catch (error) {
      if (requestId !== insurerRequestIdRef.current) return;
      console.log(error);
      setInsurerDashboard(null);
    } finally {
      if (requestId === insurerRequestIdRef.current) {
        setInsurerLoading(false);
      }
    }
  };

  useEffect(() => {
    loadInsurerDashboard(yearMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearMonth]);

  useEffect(() => {
    setInsurerBucket("all");
    setExpandedInsurer(null);
  }, [yearMonth]);

  const isInsurerDashboardCurrent = insurerDashboard?.yearMonth === yearMonth;
  const insurerSummary =
    insurerDashboard && (isInsurerDashboardCurrent || insurerLoading)
      ? insurerDashboard.summary || {}
      : {};
  const insurers =
    insurerDashboard && (isInsurerDashboardCurrent || insurerLoading)
      ? insurerDashboard.insurers || []
      : [];
  const filteredInsurers = useMemo(
    () => filterGroupsByBucket(insurers, insurerBucket),
    [insurers, insurerBucket]
  );

  const handleInsurerBucketChange = (bucketId) => {
    setInsurerBucket((current) => (current === bucketId ? "all" : bucketId));
    setExpandedInsurer(null);
  };

  const handleInsurerExportResult = async () => {
    if (!yearMonth || insurerExporting) return;

    setInsurerExporting(true);
    const result = await api.GISettlement.downloadGiOverdueDashboardResult(
      yearMonth,
      `gi-overdue-insurer-${yearMonth}.xlsx`
    );
    setInsurerExporting(false);

    if (result?.success) {
      showNotification({
        title: t("giSettlement.crossCheck.exportResult", "Export Result"),
        message: t(
          "giSettlement.crossCheck.exportSuccess",
          "Cross-check result exported"
        ),
        color: "green",
      });
      return;
    }

    showNotification({
      title: t("giSettlement.crossCheck.exportResult", "Export Result"),
      message:
        result?.error ||
        t("giSettlement.crossCheck.exportFailed", "Unable to export result"),
      color: "red",
    });
  };

  const isInsurerInitialLoad = insurerLoading && insurerDashboard === null;
  const showInsurerEmptyState =
    !insurerLoading && isInsurerDashboardCurrent && filteredInsurers.length === 0;

  return (
    <Stack spacing="md" mt="md" pb={64}>
      <Group position="apart" align="flex-start">
        <Text size="xs" c="dimmed" maw={520}>
          {t(
            "dashboard.giOverdue.helper",
            "Overdue amounts from imported insurer statements, filtered by GI settlement creation month."
          )}
        </Text>
        <Group spacing="xs">
          {insurerLoading && !isInsurerInitialLoad && <Loader size="xs" />}
          <PeriodFilter
            by="M"
            numPeriods={24}
            forward={false}
            handlePeriodChange={(p) => setPeriod(p)}
            handleRefresh={() => loadInsurerDashboard(yearMonth)}
          />
          <Button
            size="xs"
            color="green"
            leftIcon={<IconDownload size={14} />}
            disabled={insurerExporting || insurerLoading}
            loading={insurerExporting}
            onClick={handleInsurerExportResult}
          >
            {t("giSettlement.crossCheck.exportResult", "Export Result")}
          </Button>
        </Group>
      </Group>

      {isInsurerInitialLoad ? (
        <Group position="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <Box
          sx={{
            opacity: insurerLoading ? 0.6 : 1,
            transition: "opacity 150ms ease",
            pointerEvents: insurerLoading ? "none" : "auto",
          }}
        >
          <OverdueSummarySection
            summary={insurerSummary}
            activeBucket={insurerBucket}
            onBucketChange={handleInsurerBucketChange}
            summaryBuckets={GI_OVERDUE_SUMMARY_BUCKETS}
            bucketDefinitions={GI_OVERDUE_DASHBOARD_BUCKETS}
            formatter={formatter}
            t={t}
          />

          <Box mt="md">
            {showInsurerEmptyState ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                {t(
                  "dashboard.giOverdue.empty",
                  "No overdue insurer statement rows for {{month}}.",
                  { month: yearMonth }
                )}
              </Text>
            ) : (
              <OverdueGroupTable
                groups={filteredInsurers}
                nameColumnLabel={t(
                  "dashboard.giOverdue.columns.insurer",
                  "Insurer Name"
                )}
                getGroupKey={(group) => group.insurerCode || group.insurerLabel}
                getGroupLabel={(group) => group.insurerLabel || group.insurer}
                activeBucket={insurerBucket}
                expandedKey={expandedInsurer}
                onToggle={(key) =>
                  setExpandedInsurer((current) =>
                    current === key ? null : key
                  )
                }
                detailHideInsurer
                emptyMessage={t(
                  "dashboard.giOverdue.empty",
                  "No overdue insurer statement rows for {{month}}.",
                  { month: yearMonth }
                )}
                formatter={formatter}
                summaryBuckets={GI_OVERDUE_SUMMARY_BUCKETS}
                renderDetailPanel={({ group, activeBucket, hideInsurer }) => (
                  <GroupDetailPanel
                    group={group}
                    activeBucket={activeBucket}
                    hideInsurer={hideInsurer}
                    t={t}
                  />
                )}
                t={t}
              />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

const DashboardGiOutstandingConsultantTab = ({ scopeToSelf = false }) => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const [formatter] = useFormatter();
  const [cellRender] = useCellRender();
  const [consultantDashboard, setConsultantDashboard] = useState(null);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [includeBottomLine, setIncludeBottomLine] = useState(false);
  const [consultantBucket, setConsultantBucket] = useState("all");
  const [expandedConsultant, setExpandedConsultant] = useState(null);
  const [consultantFilters, setConsultantFilters] = useState(
    DEFAULT_CONSULTANT_FILTERS
  );
  const [selectedClients, setSelectedClients] = useState([]);
  const [debouncedConsultantFilters] = useDebouncedValue(
    consultantFilters,
    300
  );

  const consultantRequestIdRef = useRef(0);
  const showGroupedView = !scopeToSelf || includeBottomLine;

  const buildDashboardRequest = (filters) => ({
    paymentStatus: filters.paymentStatus,
    status: filters.status,
    policyStatus: filters.policyStatus,
    scopeToSelf,
    includeTeam: scopeToSelf && includeBottomLine,
  });

  const loadConsultantDashboard = async (filters) => {
    const requestId = ++consultantRequestIdRef.current;
    setConsultantLoading(true);
    try {
      const result = await api.DebitNote.getGiOverdueConsultantDashboard(
        buildDashboardRequest(filters)
      );
      if (requestId !== consultantRequestIdRef.current) return;
      if (result?.success !== false && result?.data) {
        setConsultantDashboard(result.data);
      } else {
        setConsultantDashboard(null);
      }
    } catch (error) {
      if (requestId !== consultantRequestIdRef.current) return;
      console.log(error);
      setConsultantDashboard(null);
    } finally {
      if (requestId === consultantRequestIdRef.current) {
        setConsultantLoading(false);
      }
    }
  };

  const downloadConsultantDashboard = async () => {
    setDownloadLoading(true);
    try {
      const result = await api.DebitNote.getGiOverdueConsultantDashboardReport(
        buildDashboardRequest(consultantFilters)
      );
      if (result?.data?.url) {
        await openFileUrl(result.data.url);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    loadConsultantDashboard(debouncedConsultantFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedConsultantFilters, includeBottomLine]);

  useEffect(() => {
    setConsultantBucket("all");
    setExpandedConsultant(null);
    setSelectedClients([]);
  }, [debouncedConsultantFilters, includeBottomLine]);

  const consultantSummary = consultantDashboard?.summary || {};
  const consultants = consultantDashboard?.consultants || [];
  const selfRows = consultantDashboard?.rows || [];
  const clientOptions = useMemo(
    () =>
      extractClientOptionsFromRows(
        getAllConsultantDashboardRows(consultantDashboard, showGroupedView)
      ),
    [consultantDashboard, showGroupedView]
  );
  const filteredConsultants = useMemo(() => {
    let groups = filterGroupsByBucket(consultants, consultantBucket);
    if (selectedClients.length) {
      groups = groups.filter(
        (g) =>
          filterRowsByClient(
            filterRowsByOverdueBucket(
              g.rows,
              consultantBucket,
              GI_CONSULTANT_OVERDUE_BUCKETS
            ),
            selectedClients
          ).length > 0
      );
    }
    return groups;
  }, [consultants, consultantBucket, selectedClients]);
  const filteredSelfRows = useMemo(
    () =>
      filterRowsByClient(
        filterRowsByOverdueBucket(
          selfRows,
          consultantBucket,
          GI_CONSULTANT_OVERDUE_BUCKETS
        ),
        selectedClients
      ),
    [selfRows, consultantBucket, selectedClients]
  );

  const handleConsultantBucketChange = (bucketId) => {
    setConsultantBucket((current) => (current === bucketId ? "all" : bucketId));
    setExpandedConsultant(null);
  };

  const isConsultantInitialLoad =
    consultantLoading && consultantDashboard === null;
  const showConsultantEmptyState = showGroupedView
    ? !consultantLoading && filteredConsultants.length === 0
    : !consultantLoading && filteredSelfRows.length === 0;

  return (
    <Stack spacing="md" mt="md" pb={64}>
      <Text size="xs" c="dimmed" maw={520}>
        {scopeToSelf
          ? t(
              "dashboard.giOutstanding.helperSelf",
              "Your current unpaid debit notes. Not tied to settlement month."
            )
          : t(
              "dashboard.giOverdue.helperConsultant",
              "Current unpaid debit notes by consultant. Not tied to settlement month."
            )}
      </Text>

      {isConsultantInitialLoad ? (
        <Group position="center" py="xl">
          <Loader size="sm" />
        </Group>
      ) : (
        <Box
          sx={{
            opacity: consultantLoading ? 0.6 : 1,
            transition: "opacity 150ms ease",
            pointerEvents: consultantLoading ? "none" : "auto",
          }}
        >
          <OverdueSummarySection
            summary={consultantSummary}
            activeBucket={consultantBucket}
            onBucketChange={handleConsultantBucketChange}
            summaryBuckets={GI_CONSULTANT_SUMMARY_BUCKETS}
            bucketDefinitions={GI_CONSULTANT_OVERDUE_BUCKETS}
            formatter={formatter}
            t={t}
          />

          <GiOverdueConsultantFilterBar
            filters={consultantFilters}
            onChange={setConsultantFilters}
            clientOptions={clientOptions}
            selectedClients={selectedClients}
            onClientChange={setSelectedClients}
            onRefresh={() => loadConsultantDashboard(consultantFilters)}
            onDownload={downloadConsultantDashboard}
            loading={consultantLoading}
            downloadLoading={downloadLoading}
            showIncludeBottomLine={scopeToSelf}
            includeBottomLine={includeBottomLine}
            onIncludeBottomLineChange={setIncludeBottomLine}
            t={t}
          />

          <Box mt="md">
            {showConsultantEmptyState ? (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                {t(
                  !showGroupedView
                    ? "dashboard.giOverdue.emptySelf"
                    : "dashboard.giOverdue.emptyConsultant",
                  !showGroupedView
                    ? "No unpaid debit notes match your current filters."
                    : "No unpaid debit notes match the current filters."
                )}
              </Text>
            ) : !showGroupedView ? (
              <ConsultantDetailPanel
                group={{ rows: selfRows }}
                activeBucket={consultantBucket}
                selectedClients={selectedClients}
                formatter={formatter}
                cellRender={cellRender}
                t={t}
              />
            ) : (
              <OverdueGroupTable
                groups={filteredConsultants}
                nameColumnLabel={t(
                  "dashboard.giOverdue.columns.consultant",
                  "Consultant Name"
                )}
                getGroupKey={(group) =>
                  group.consultantId || group.consultantLabel
                }
                getGroupLabel={(group) =>
                  group.consultantId
                    ? group.consultantLabel
                    : t("dashboard.giOverdue.unassigned", "Unassigned")
                }
                activeBucket={consultantBucket}
                expandedKey={expandedConsultant}
                onToggle={(key) =>
                  setExpandedConsultant((current) =>
                    current === key ? null : key
                  )
                }
                detailHideInsurer={false}
                emptyMessage={t(
                  "dashboard.giOverdue.emptyConsultant",
                  "No unpaid debit notes match the current filters."
                )}
                formatter={formatter}
                summaryBuckets={GI_CONSULTANT_SUMMARY_BUCKETS}
                renderDetailPanel={({ group, activeBucket }) => (
                  <ConsultantDetailPanel
                    group={group}
                    activeBucket={activeBucket}
                    selectedClients={selectedClients}
                    formatter={formatter}
                    cellRender={cellRender}
                    t={t}
                  />
                )}
                t={t}
              />
            )}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

export {
  DashboardGiOutstandingInsurerTab,
  DashboardGiOutstandingConsultantTab,
};
